import { cityById } from "./cities.ts";
import { cholesky } from "./linalg.ts";
import { CRORE, LAKH } from "./types.ts";
import type { PlanInputs } from "./types.ts";
import { DEFAULTS } from "./defaults.ts";

/**
 * Every numeric input has a range it is allowed to take. Two things depend on
 * this: the UI, which shows the range and clamps on blur, and `sanitize()`,
 * which is the real guard — it runs on anything arriving from a shared URL,
 * where the UI never got a say.
 *
 * Ranges are wide enough not to argue with a reasonable user and narrow enough
 * that the simulation cannot be handed something meaningless.
 */
export interface Limit {
  min: number;
  max: number;
  step: number;
  /** How to describe the range in a hint, in the user's units. */
  hint: string;
}

export const LIMITS = {
  adultAge: { min: 30, max: 80, step: 1, hint: "30 to 80" },
  childAge: { min: 0, max: 25, step: 1, hint: "0 to 25" },
  parentCount: { min: 0, max: 4, step: 1, hint: "0 to 4" },
  parentAge: { min: 55, max: 100, step: 1, hint: "55 to 100" },
  yearsToRetirement: { min: 0, max: 30, step: 1, hint: "0 to 30" },
  horizonYears: { min: 10, max: 65, step: 1, hint: "10 to 65" },

  lifestylePerYear: { min: 6 * LAKH, max: 5 * CRORE, step: LAKH, hint: "₹6 L to ₹5 cr" },
  schoolPerChildPerYear: { min: 0, max: 50 * LAKH, step: 50_000, hint: "₹0 to ₹50 L" },
  undergradPerYear: { min: 0, max: 2 * CRORE, step: LAKH, hint: "₹0 to ₹2 cr" },
  undergradYears: { min: 0, max: 8, step: 1, hint: "0 to 8" },
  undergradStartAge: { min: 15, max: 25, step: 1, hint: "15 to 25" },
  healthCoverPerYear: { min: 0, max: 25 * LAKH, step: 10_000, hint: "₹0 to ₹25 L" },
  elderPerYear: { min: 0, max: 1 * CRORE, step: 50_000, hint: "₹0 to ₹1 cr" },
  cityIndex: { min: 0.4, max: 1.3, step: 0.01, hint: "0.40 to 1.30" },
  localShare: { min: 0, max: 1, step: 0.05, hint: "0% to 100%" },

  inflationGeneral: { min: 0, max: 0.2, step: 0.005, hint: "0% to 20%" },
  inflationVol: { min: 0, max: 0.1, step: 0.005, hint: "0% to 10%" },
  inflationBucket: { min: 0, max: 0.3, step: 0.005, hint: "0% to 30%" },
  equityCorrelation: { min: -1, max: 1, step: 0.05, hint: "-1 to 1" },

  assetReturn: { min: -0.05, max: 0.3, step: 0.005, hint: "-5% to 30%" },
  assetVol: { min: 0.001, max: 0.6, step: 0.005, hint: "0.1% to 60%" },
  allocation: { min: 0, max: 1, step: 0.01, hint: "0% to 100%" },
  glideYears: { min: 0, max: 50, step: 1, hint: "0 to 50" },

  remoteAccessPerYear: { min: 0, max: 5 * LAKH, step: 5_000, hint: "₹0 to ₹5 L" },
  taxDrag: { min: 0, max: 0.45, step: 0.005, hint: "0% to 45%" },
  simulations: { min: 1_000, max: 100_000, step: 1_000, hint: "1,000 to 100,000" },
} as const satisfies Record<string, Limit>;

export type LimitKey = keyof typeof LIMITS;

/** Oldest age the plan will model to. Beyond this the horizon is capped. */
export const MAX_PLANNING_AGE = 110;

/**
 * Coerce a value into a limit. Anything that is not a finite number — an empty
 * field, a stray letter, a hand-edited URL — falls back rather than becoming
 * NaN, which would otherwise propagate silently through the whole simulation
 * and surface as a blank result.
 */
export function clamp(value: unknown, limit: Limit, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, limit.min), limit.max);
}

function clampInt(value: unknown, limit: Limit, fallback: number): number {
  return Math.round(clamp(value, limit, fallback));
}

/** Weights must sum to 1, or the portfolio is silently leveraged or part-cash. */
function normalise(
  w: readonly number[],
  fallback: readonly number[],
): [number, number, number, number] {
  const cleaned = w.map((v, i) => clamp(v, LIMITS.allocation, fallback[i]));
  const total = cleaned.reduce((a, b) => a + b, 0);
  if (total <= 0) return [fallback[0], fallback[1], fallback[2], fallback[3]];

  // Already effectively normalised: leave the numbers exactly as they are.
  // Dividing by a sum that is 0.9999999999999999 rather than 1 shifts every
  // weight by a few ulps, so repeated sanitize() calls would never settle and
  // React would re-render forever.
  if (Math.abs(total - 1) < 1e-6) return [cleaned[0], cleaned[1], cleaned[2], cleaned[3]];

  const out = cleaned.map((v) => Math.round((v / total) * 1e6) / 1e6);
  return [out[0], out[1], out[2], out[3]];
}

export interface SanitizeResult {
  inputs: PlanInputs;
  /** Human-readable notes about anything that had to be adjusted. */
  notices: string[];
}

/**
 * Force a plan into the range of things the model can meaningfully answer.
 * Called on every edit and on anything decoded from a URL. Returns the notices
 * so the UI can say what it changed rather than silently overriding the user.
 */
export function sanitize(raw: PlanInputs): SanitizeResult {
  const d = DEFAULTS;
  const notices: string[] = [];
  const note = (before: number, after: number, msg: string) => {
    if (Math.abs(before - after) > 1e-9 || !Number.isFinite(before)) notices.push(msg);
  };

  // ---- household
  const adultAge = clampInt(raw.household?.adultAge, LIMITS.adultAge, d.household.adultAge);
  note(raw.household?.adultAge, adultAge, `Retirement age set to ${adultAge} (allowed ${LIMITS.adultAge.hint}).`);

  const childAges = (Array.isArray(raw.household?.childAges) ? raw.household.childAges : [])
    .slice(0, 6)
    .map((a) => clampInt(a, LIMITS.childAge, 8));

  const parentCount = clampInt(raw.household?.parentCount, LIMITS.parentCount, d.household.parentCount);
  const parentAge = clampInt(raw.household?.parentAge, LIMITS.parentAge, d.household.parentAge);
  note(raw.household?.parentAge, parentAge, `Parents' age set to ${parentAge} (allowed ${LIMITS.parentAge.hint}).`);

  // ---- plan span
  const yearsToRetirement = clampInt(raw.yearsToRetirement, LIMITS.yearsToRetirement, d.yearsToRetirement);
  let horizonYears = clampInt(raw.horizonYears, LIMITS.horizonYears, d.horizonYears);
  const maxHorizon = Math.max(LIMITS.horizonYears.min, MAX_PLANNING_AGE - adultAge);
  if (horizonYears > maxHorizon) {
    notices.push(
      `Horizon shortened to ${maxHorizon} years — retiring at ${adultAge}, that already reaches age ${MAX_PLANNING_AGE}.`,
    );
    horizonYears = maxHorizon;
  }

  // ---- spending
  const s = raw.spending ?? d.spending;
  const lifestylePerYear = clamp(s.lifestylePerYear, LIMITS.lifestylePerYear, d.spending.lifestylePerYear);
  note(s.lifestylePerYear, lifestylePerYear, `Lifestyle spend set within ${LIMITS.lifestylePerYear.hint}.`);

  const undergradStartAge = clampInt(s.undergradStartAge, LIMITS.undergradStartAge, d.spending.undergradStartAge);
  const elderEndAge = clampInt(s.elderEndAge, LIMITS.parentAge, d.spending.elderEndAge);

  const rampSource = Array.isArray(s.elderRamp) && s.elderRamp.length ? s.elderRamp : d.spending.elderRamp;
  const elderRamp = rampSource
    .slice(0, 6)
    .map((step, i) => ({
      fromAge: clampInt(step?.fromAge, { min: 0, max: 120, step: 1, hint: "" }, d.spending.elderRamp[i]?.fromAge ?? 0),
      perYear: clamp(step?.perYear, LIMITS.elderPerYear, d.spending.elderRamp[i]?.perYear ?? 0),
    }))
    .sort((a, b) => a.fromAge - b.fromAge);

  const spending = {
    lifestylePerYear,
    lifestylePricedLocally: s.lifestylePricedLocally === true,
    schoolPerChildPerYear: clamp(s.schoolPerChildPerYear, LIMITS.schoolPerChildPerYear, d.spending.schoolPerChildPerYear),
    undergradPerYear: clamp(s.undergradPerYear, LIMITS.undergradPerYear, d.spending.undergradPerYear),
    undergradYears: clampInt(s.undergradYears, LIMITS.undergradYears, d.spending.undergradYears),
    undergradStartAge,
    healthCoverPerYear: clamp(s.healthCoverPerYear, LIMITS.healthCoverPerYear, d.spending.healthCoverPerYear),
    elderRamp,
    elderEndAge,
    cityIndex: clamp(s.cityIndex, LIMITS.cityIndex, d.spending.cityIndex),
    localShare: clamp(s.localShare, LIMITS.localShare, d.spending.localShare),
    // An unknown city id (an old link, a renamed entry) resolves to Bengaluru
    // rather than leaving the plan without a city at all.
    cityId: cityById(typeof s.cityId === "string" ? s.cityId : d.spending.cityId).id,
    remoteAccessPerYear: clamp(
      s.remoteAccessPerYear,
      LIMITS.remoteAccessPerYear,
      d.spending.remoteAccessPerYear,
    ),
  };

  // ---- inflation
  const inf = raw.inflation ?? d.inflation;
  const inflation = {
    general: clamp(inf.general, LIMITS.inflationGeneral, d.inflation.general),
    generalVol: clamp(inf.generalVol, LIMITS.inflationVol, d.inflation.generalVol),
    equityCorrelation: clamp(inf.equityCorrelation, LIMITS.equityCorrelation, d.inflation.equityCorrelation),
    life: clamp(inf.life, LIMITS.inflationBucket, d.inflation.life),
    school: clamp(inf.school, LIMITS.inflationBucket, d.inflation.school),
    undergrad: clamp(inf.undergrad, LIMITS.inflationBucket, d.inflation.undergrad),
    medical: clamp(inf.medical, LIMITS.inflationBucket, d.inflation.medical),
  };

  // ---- markets
  const m = raw.markets ?? d.markets;
  const mu = (Array.isArray(m.mu) ? m.mu : d.markets.mu).map((v, i) =>
    clamp(v, LIMITS.assetReturn, d.markets.mu[i]),
  ) as [number, number, number, number];
  const sigma = (Array.isArray(m.sigma) ? m.sigma : d.markets.sigma).map((v, i) =>
    clamp(v, LIMITS.assetVol, d.markets.sigma[i]),
  ) as [number, number, number, number];

  const allocStart = normalise(m.allocStart ?? d.markets.allocStart, d.markets.allocStart);
  const allocEnd = normalise(m.allocEnd ?? d.markets.allocEnd, d.markets.allocEnd);
  const startSum = (m.allocStart ?? []).reduce((a: number, b: number) => a + b, 0);
  if (Number.isFinite(startSum) && startSum > 0 && Math.abs(startSum - 1) > 0.005) {
    notices.push("Starting allocation rescaled to total 100%.");
  }

  // A correlation matrix the user edited can stop being positive definite, which
  // makes the Cholesky factor imaginary. Fall back rather than throw at runtime.
  let correlation = Array.isArray(m.correlation) ? m.correlation : d.markets.correlation;
  const wellFormed =
    correlation.length === 4 && correlation.every((r) => Array.isArray(r) && r.length === 4);
  if (!wellFormed) {
    correlation = d.markets.correlation;
  } else {
    try {
      cholesky(correlation);
    } catch {
      notices.push("Correlation matrix was not valid, so the defaults were restored.");
      correlation = d.markets.correlation;
    }
  }

  const annualTax = (Array.isArray(m.annualTax) ? m.annualTax : d.markets.annualTax).map((v, i) =>
    clamp(v, { min: 0, max: 0.6, step: 0.005, hint: "0% to 60%" }, d.markets.annualTax[i]),
  ) as [number, number, number, number];

  const markets = {
    mu,
    sigma,
    correlation,
    annualTax,
    presetId: typeof m.presetId === "string" ? m.presetId : d.markets.presetId,
    allocStart,
    allocEnd,
    glideYears: clampInt(m.glideYears, LIMITS.glideYears, d.markets.glideYears),
  };

  // ---- method
  const taxDrag = clamp(raw.taxDrag, LIMITS.taxDrag, d.taxDrag);
  note(raw.taxDrag, taxDrag, `Tax drag set within ${LIMITS.taxDrag.hint}.`);
  const simulations = clampInt(raw.simulations, LIMITS.simulations, d.simulations);
  const seed = Number.isFinite(raw.seed) ? Math.abs(Math.round(raw.seed)) % 2 ** 31 : d.seed;

  return {
    inputs: {
      household: { adultAge, childAges, parentCount, parentAge },
      spending,
      inflation,
      markets,
      taxDrag,
      horizonYears,
      yearsToRetirement,
      guardrails: raw.guardrails !== false,
      simulations,
      seed,
    },
    notices,
  };
}
